import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, MapPin, Globe, Instagram, Mail, Phone, Users,
  Clock, ArrowLeft, ExternalLink, Music2, Disc3,
} from 'lucide-react';
import Navbar from '../Navbar';
import SEO from '../SEO';

// Corporate public profile for DJ agencies: agency info + full roster grid
export default function AgencyPublicView({ agency, roster = [], identifier }) {
  const contacts = [
    agency.website && { icon: Globe, label: 'Sitio Web', href: agency.website.startsWith('http') ? agency.website : `https://${agency.website}` },
    agency.instagram && { icon: Instagram, label: agency.instagram, href: `https://instagram.com/${agency.instagram.replace('@', '')}` },
    agency.email_contact && { icon: Mail, label: agency.email_contact, href: `mailto:${agency.email_contact}` },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#030303] w-full" data-testid="agency-public-view">
      <SEO
        title={`${agency.artistic_name} - Agencia de DJs`}
        description={agency.bio?.substring(0, 160) || `${agency.artistic_name} - Agencia de DJs en Bravo`}
        image={agency.photo_url || agency.banner_url}
        url={`/agencia/${agency.slug || identifier}`}
        type="profile"
      />
      <Navbar />

      {/* Hero */}
      <div className="relative">
        <div className="h-[180px] sm:h-[260px] relative overflow-hidden">
          {agency.banner_url ? (
            <img src={agency.banner_url} alt={agency.artistic_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/25 via-[#121212] to-[#030303]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/40 to-transparent" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 sm:-mt-20 relative z-10">
          <Link to="/agencias" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs mb-4 transition-colors">
            <ArrowLeft size={14} /> Directorio de Agencias
          </Link>
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden border-2 border-[#D4AF37]/60 bg-[#121212] flex items-center justify-center flex-shrink-0 shadow-[0_0_40px_rgba(212,175,55,0.25)]"
            >
              {agency.photo_url ? (
                <img src={agency.photo_url} alt={agency.artistic_name} className="w-full h-full object-cover" />
              ) : (
                <Building2 size={48} className="text-[#D4AF37]" />
              )}
            </motion.div>
            <div className="flex-1 text-center sm:text-left pb-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black uppercase tracking-widest mb-2">
                <Building2 size={11} /> Agencia de DJs
              </span>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white"
              >
                {agency.artistic_name}
              </motion.h1>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1.5 text-white/60 text-xs sm:text-sm mt-2">
                {agency.city && (
                  <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#D4AF37]" /> {agency.city}</span>
                )}
                <span className="flex items-center gap-1.5"><Users size={14} className="text-[#D4AF37]" /> {roster.length} DJ{roster.length !== 1 ? 's' : ''} en el roster</span>
                {agency.experience_years ? (
                  <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#D4AF37]" /> {agency.experience_years}+ años en la industria</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10">
        {/* About + contact */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            {agency.bio && (
              <div className="bg-[#0D0D0D] border border-white/5 rounded-2xl p-6">
                <h2 className="text-sm font-black uppercase tracking-widest text-[#D4AF37] mb-3 flex items-center gap-2">
                  <Building2 size={14} /> Sobre la Agencia
                </h2>
                <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line">{agency.bio}</p>
              </div>
            )}
          </div>
          <div className="bg-[#0D0D0D] border border-[#D4AF37]/20 rounded-2xl p-6 h-fit">
            <h2 className="text-sm font-black uppercase tracking-widest text-[#D4AF37] mb-4">Contacto</h2>
            <div className="space-y-3">
              {contacts.map((c, i) => (
                <a key={i} href={c.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-[#D4AF37] text-sm transition-colors">
                  <c.icon size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="truncate">{c.label}</span>
                </a>
              ))}
              {agency.phone && (
                <span className="flex items-center gap-3 text-white/70 text-sm">
                  <Phone size={16} className="text-[#D4AF37] flex-shrink-0" /> {agency.phone}
                </span>
              )}
              {contacts.length === 0 && !agency.phone && (
                <p className="text-white/40 text-xs">Sin datos de contacto públicos</p>
              )}
            </div>
          </div>
        </div>

        {/* Roster */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
              <Disc3 size={22} className="text-[#D4AF37]" /> Nuestro Roster
            </h2>
            <span className="px-2.5 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold">{roster.length}</span>
          </div>
          {roster.length === 0 ? (
            <p className="text-white/40 text-sm">La agencia todavía no publicó DJs en su roster.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="agency-roster-grid">
              {roster.map((dj, idx) => (
                <motion.div
                  key={dj.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link
                    to={`/dj/${dj.slug || dj.id}`}
                    className="group block bg-[#0D0D0D] border border-white/5 hover:border-[#D4AF37]/50 rounded-2xl overflow-hidden transition-colors"
                    data-testid={`roster-dj-card-${dj.id}`}
                  >
                    <div className="aspect-square bg-[#121212] relative overflow-hidden">
                      {dj.photo_url ? (
                        <img src={dj.photo_url} alt={dj.artistic_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music2 size={36} className="text-[#D4AF37]/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <ExternalLink size={14} className="absolute top-3 right-3 text-white/0 group-hover:text-white/70 transition-colors" />
                    </div>
                    <div className="p-3">
                      <p className="font-bold text-white text-sm truncate">{dj.artistic_name}</p>
                      <p className="text-[11px] text-white/40 truncate mt-0.5">
                        {(dj.genres || []).slice(0, 2).join(' · ') || dj.city}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
